import Accordion from "components/accordion/Accordion";
import DailyReportCard from "components/card/DailyReportCard";
import { CreateAppealRequestPopup } from "components/pop-up/AppealRequest";
import { useState, useMemo } from "react";

import { AssetAppeal, Report } from "types";

export default function ReportList({
  referralId,
  fileRowProps,
  reports,
  recoveryReports,
  appeal,
  handleReportDataUpdated,
}: {
  referralId?: string;
  fileRowProps: any;
  reports: Report[];
  recoveryReports: Report[];
  appeal?: AssetAppeal;
  handleReportDataUpdated?: () => void;
}) {
  // Filter recovery reports by type
  const recoveryDailyReports = recoveryReports.filter(
    (report) => !report.is_next_appointment_reason_final_verdict,
  );
  const recoveryFinalVerdicts = recoveryReports.filter(
    (report) => report.is_next_appointment_reason_final_verdict,
  );

  const [isAppealPopupOpen, setIsAppealPopupOpen] = useState<{
    open: boolean;
    data: any;
  }>({ open: false, data: null });

  // Collect all assets from final verdict reports
  const allFinalReportAssets = useMemo(() => {
    const assets: any[] = [];
    recoveryFinalVerdicts.forEach((report) => {
      if (report.asset) {
        if (Array.isArray(report.asset)) {
          assets.push(...report.asset);
        } else if (typeof report.asset === "object") {
          assets.push(report.asset);
        }
      }
    });
    return assets;
  }, [recoveryFinalVerdicts]);

  return (
    <div className="max-w-3xl bg-gray-50 rounded-xl shadow-sm w-[55%] p-2 mt-2">
      <div className="space-y-2">
        {/* Asset Recovery Daily Reports */}
        {recoveryDailyReports.length > 0 && (
          <Accordion
            title={`Asset Recovery Daily Reports (${recoveryDailyReports.length})`}
            mainTitle=""
          >
            <div className="space-y-2">
              {recoveryDailyReports.map((report, index) => (
                <Accordion
                  key={report.id}
                  title={`Daily Report - ${new Date(
                    report.court_hearing_date,
                  ).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}`}
                  mainTitle=""
                >
                  <DailyReportCard
                    key={report.id || index}
                    report={report}
                    fileRowProps={fileRowProps}
                    setIsAppealPopupOpen={setIsAppealPopupOpen}
                    prohibitionId={report.prohabition_request}
                  />
                </Accordion>
              ))}
            </div>
          </Accordion>
        )}

        {/* Asset Recovery Final Verdicts */}
        {recoveryFinalVerdicts.length > 0 && (
          <Accordion
            title={`Asset Recovery Final Verdicts (${recoveryFinalVerdicts.length})`}
            mainTitle=""
          >
            <div className="space-y-2">
              {recoveryFinalVerdicts.map((report, index) => (
                <Accordion
                  key={report.id}
                  title={`Final Verdict - ${new Date(
                    report.court_decision_date as string,
                  ).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}`}
                  mainTitle=""
                >
                  <DailyReportCard
                    key={report.id || index}
                    report={report}
                    fileRowProps={fileRowProps}
                    isAssetRecoveryReport={true}
                    setIsAppealPopupOpen={setIsAppealPopupOpen}
                    appeal={appeal}
                    prohibitionId={report.prohabition_request}
                  />
                </Accordion>
              ))}
            </div>
          </Accordion>
        )}

        {/* No Reports Message */}
        {recoveryReports.length === 0 && <p>No Recovery Reports Added</p>}
      </div>
      <CreateAppealRequestPopup
        referralId={referralId as string}
        data={isAppealPopupOpen.data}
        allAssets={allFinalReportAssets} // Pass all final report assets
        isOpen={isAppealPopupOpen.open}
        onClose={() => setIsAppealPopupOpen({ open: false, data: null })}
        onSuccess={handleReportDataUpdated}
      />
    </div>
  );
}
